import React from 'react';
import { ChevronDown, Loader2, Search, PanelTop } from 'lucide-react';
import { DIAGRAM_PALETTE } from '../../../utils/diagramTag';

type Tr = (key: string, def: string, vars?: Record<string, unknown>) => string;

// ── Category row + illustration tabs (the question block's last lines) ──────
// ONE bordered row: parent group › category name, its code, and a
// "CATEGORY 1 OF 3" pill beside the chevron that opens the other categories
// that may hold the part. Under it, indented behind a vertical rule so they
// read as a level BELOW the category, the category's illustrations: number,
// the drawing's title with its code beneath, or the code and "No title in
// catalog" when the vendor gave none.

export interface CategoryItem {
  key: string;
  name: string;
  parent?: string | null;
  /** Candidate parts in it. */
  count?: number | null;
  current?: boolean;
}

export interface IllustrationTab {
  index: number;
  title?: string | null;
  code?: string | null;
}

interface Props {
  tr: Tr;
  parent?: string | null;
  name: string;
  code?: string | null;
  /** Every category that may hold the part, current one included; null = still loading. */
  categories: CategoryItem[] | null;
  onOpenMenu?: () => void;
  onPick?: (item: CategoryItem) => void;
  onSearchCatalog?: () => void;
  illustrations: IllustrationTab[];
  activeIllustration: number;
  onIllustration: (index: number) => void;
}

const CategorySwitcher: React.FC<Props> = ({
  tr, parent, name, code, categories, onOpenMenu, onPick, onSearchCatalog,
  illustrations, activeIllustration, onIllustration,
}) => {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);
  const list = categories || [];
  const at = list.findIndex(c => c.current);
  const total = list.length;
  const switchable = !!onPick && total > 1;
  const hint = tr('chat.clarification.categoryTooltip', 'Not the part you need? Switch to another category that may hold it.');
  return (
    <div data-testid="category-switcher" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ position: 'relative', minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" data-testid="diagram-catalog-context" aria-haspopup="menu" aria-expanded={open} title={hint}
          onClick={() => { setOpen(v => !v); onOpenMenu?.(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0, height: 36, boxSizing: 'border-box', padding: '0 12px', border: `1px solid ${open ? DIAGRAM_PALETTE.navy : '#e3e2e8'}`, borderRadius: 9, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
          <PanelTop size={15} aria-hidden style={{ flexShrink: 0, color: DIAGRAM_PALETTE.textMuted }} />
          <span style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, lineHeight: '18px' }}>
            {parent && <span style={{ color: '#6c6978', fontWeight: 500 }}>{parent} <span aria-hidden style={{ color: '#b1b3bd' }}>›</span> </span>}
            <span data-testid="category-name" style={{ color: DIAGRAM_PALETTE.textPrimary, fontWeight: 700 }}>{name}</span>
          </span>
          {code && (
            <code style={{ flexShrink: 0, fontFamily: 'inherit', fontSize: 11.5, lineHeight: '16px', fontVariantNumeric: 'tabular-nums', color: '#6c6978', background: '#f4f4f7', borderRadius: 5, padding: '1px 7px', whiteSpace: 'nowrap' }}>{code}</code>
          )}
          <span style={{ flex: 1, minWidth: 8 }} />
          {categories == null ? (
            <Loader2 size={15} className="animate-spin" aria-label={tr('common.loading', 'Loading…')} style={{ flexShrink: 0, color: DIAGRAM_PALETTE.textMuted }} />
          ) : total > 1 ? (
            <span data-testid="category-of" style={{ flexShrink: 0, fontSize: 10.5, lineHeight: '16px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: DIAGRAM_PALETTE.indigo, background: DIAGRAM_PALETTE.tagBg, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>
              {tr('chat.clarification.categoryOf', 'Category {{n}} of {{m}}', { n: at >= 0 ? at + 1 : 1, m: total })}
            </span>
          ) : null}
          <ChevronDown size={15} aria-hidden style={{ flexShrink: 0, color: DIAGRAM_PALETTE.textPrimary, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
        </button>
        {open && (
          <div role="menu" data-testid="category-menu" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', width: 360, maxWidth: '90vw', background: DIAGRAM_PALETTE.bgSurface, border: `1px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(20,22,40,0.14)', padding: 6, zIndex: 30 }}>
            <div style={{ fontSize: 11, color: DIAGRAM_PALETTE.textMuted, padding: '6px 8px', lineHeight: 1.4 }}>{hint}</div>
            {categories == null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, fontSize: 12, color: DIAGRAM_PALETTE.textMuted }}>
                <Loader2 size={13} className="animate-spin" />{tr('common.loading', 'Loading…')}
              </div>
            )}
            {categories != null && !switchable && (
              <div style={{ padding: '4px 8px 8px', fontSize: 11.5, color: DIAGRAM_PALETTE.textMuted, lineHeight: 1.5 }}>
                {tr('chat.clarification.onlySection', 'This is the only section holding parts like this one.')}
              </div>
            )}
            {switchable && list.map((c, i) => (
              <button key={c.key} type="button" role="menuitemradio" aria-checked={!!c.current}
                onClick={() => { setOpen(false); if (!c.current) onPick?.(c); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 8, borderWidth: 0, borderStyle: 'none', borderRadius: 7, textAlign: 'left', cursor: c.current ? 'default' : 'pointer', background: c.current ? DIAGRAM_PALETTE.selBg : 'transparent', color: DIAGRAM_PALETTE.textPrimary, fontFamily: 'inherit' }}>
                <span style={{ width: 18, fontSize: 11, fontWeight: 700, color: DIAGRAM_PALETTE.textMuted, flexShrink: 0 }}>{i + 1}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: c.current ? 700 : 600, lineHeight: 1.3 }}>
                  {c.parent && <span style={{ fontWeight: 500, color: DIAGRAM_PALETTE.textMuted }}>{c.parent} › </span>}{c.name}
                </span>
                {!!c.count && (
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: DIAGRAM_PALETTE.textMuted, background: DIAGRAM_PALETTE.bgSubtle, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    {tr('chat.clarification.categoryCandidates', '{{count}} candidates', { count: c.count })}
                  </span>
                )}
              </button>
            ))}
            {onSearchCatalog && (
              <>
                <div style={{ borderTop: `1px solid ${DIAGRAM_PALETTE.border}`, marginTop: 4 }} />
                <button type="button" role="menuitem" onClick={() => { setOpen(false); onSearchCatalog(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 8, borderWidth: 0, borderStyle: 'none', background: 'transparent', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: DIAGRAM_PALETTE.textPrimary, fontFamily: 'inherit' }}>
                  <Search size={13} />{tr('chat.clarification.searchWholeCatalog', 'Search the whole catalog…')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {illustrations.length > 1 && (
        <div role="tablist" aria-label={tr('chat.clarification.illustrations', 'Illustrations')} data-testid="illustration-tabs"
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 18, paddingLeft: 12, borderLeft: `2px solid #e3e2e8`, overflowX: 'auto', minWidth: 0 }}>
          {/* The strip says there are several drawings, like "Category 1 of 3" does for categories. */}
          <span data-testid="illustration-of" style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6c6978', marginRight: 4, whiteSpace: 'nowrap' }}>
            {tr('chat.clarification.illustrationOf', 'Illustration {{n}} of {{m}}', { n: Math.max(1, illustrations.findIndex(i => i.index === activeIllustration) + 1), m: illustrations.length })}
          </span>
          {illustrations.map(il => {
            const on = il.index === activeIllustration;
            const title = (il.title || '').trim();
            const primary = title || il.code || tr('chat.clarification.illustrationN', 'Illustration {{n}}', { n: il.index });
            const secondary = title ? (il.code || '') : tr('chat.clarification.noTitleInCatalog', 'No title in catalog');
            return (
              <button key={il.index} type="button" role="tab" aria-selected={on} data-testid="illustration-tab" onClick={() => onIllustration(il.index)}
                title={`${tr('chat.clarification.illustrationN', 'Illustration {{n}}', { n: il.index })} · ${primary}`}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 10, padding: '6px 14px 6px 8px', borderRadius: 10, cursor: on ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', textAlign: 'left',
                  border: `1px solid ${on ? '#e3e2e8' : 'transparent'}`, background: on ? '#fff' : 'transparent', boxShadow: 'none' }}>
                <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', background: on ? DIAGRAM_PALETTE.tagBg : '#f0eff4', color: on ? DIAGRAM_PALETTE.indigo : '#6c6978' }}>{il.index}</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ fontSize: 13.5, fontWeight: on ? 700 : 600, color: on ? DIAGRAM_PALETTE.textPrimary : '#4a4856' }}>{primary}</span>
                  {secondary && <span style={{ fontSize: 11.5, color: '#6c6978' }}>{secondary}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategorySwitcher;
